import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { getAssetSettings } from '../services/assetService';
import { ALL_ASSET_TYPES, AssetSettings, AssetType } from '../types/Asset';

type AssetSettingsContextValue = {
  settings: AssetSettings | null;
  availableTypes: AssetType[];
  enabledTypes: AssetType[];
  isModuleEnabled: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
};

const emptySettings: AssetSettings = {
  id: '',
  availableTypes: [...ALL_ASSET_TYPES],
  enabledTypes: [],
};

const AssetSettingsContext = createContext<AssetSettingsContextValue | undefined>(undefined);

export const AssetSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const canReadAssets = hasPermission(PermissionScopes.assetsRead);
  const [settings, setSettings] = useState<AssetSettings | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !canReadAssets) {
      setSettings(null);
      return;
    }

    setLoading(true);
    try {
      setSettings(await getAssetSettings());
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [canReadAssets, isAuthenticated]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    void refresh();
  }, [authLoading, refresh]);

  const value = useMemo<AssetSettingsContextValue>(() => {
    const resolved = settings ?? emptySettings;
    return {
      settings,
      availableTypes: resolved.availableTypes,
      enabledTypes: resolved.enabledTypes,
      isModuleEnabled: resolved.enabledTypes.length > 0,
      loading,
      refresh,
    };
  }, [loading, refresh, settings]);

  return <AssetSettingsContext.Provider value={value}>{children}</AssetSettingsContext.Provider>;
};

export const useAssetSettings = (): AssetSettingsContextValue => {
  const context = useContext(AssetSettingsContext);
  if (!context) {
    throw new Error('useAssetSettings must be used within an AssetSettingsProvider');
  }
  return context;
};
